import { useCallback, useEffect, useRef, useState } from 'react'

export const KIOSK_IDLE_MS = 60_000
export const KIOSK_COUNTDOWN_SECONDS = 15

export function createKioskIdleTimer(
  onCountdown: (seconds: number | null) => void,
  onClose: () => void,
): { reset: () => void; hold: (held: boolean) => void; stop: () => void } {
  let idle: ReturnType<typeof setTimeout> | undefined
  let countdown: ReturnType<typeof setInterval> | undefined
  let held = false
  const stop = () => {
    if (idle !== undefined) clearTimeout(idle)
    if (countdown !== undefined) clearInterval(countdown)
    idle = undefined
    countdown = undefined
  }
  const reset = () => {
    stop()
    onCountdown(null)
    if (held) return
    idle = setTimeout(() => {
      let seconds = KIOSK_COUNTDOWN_SECONDS
      onCountdown(seconds)
      countdown = setInterval(() => {
        seconds -= 1
        if (seconds === 0) {
          stop()
          onClose()
          return
        }
        onCountdown(seconds)
      }, 1000)
    }, KIOSK_IDLE_MS)
  }
  const hold = (next: boolean) => {
    if (held === next) return
    held = next
    reset()
  }
  reset()
  return { reset, hold, stop }
}

export function useKioskIdle(
  open: boolean,
  onClose: () => void,
): {
  countdown: number | null
  reset: () => void
  hold: (held: boolean) => void
} {
  const [countdown, setCountdown] = useState<number | null>(null)
  const timer = useRef<ReturnType<typeof createKioskIdleTimer> | null>(null)
  useEffect(() => {
    if (!open) return
    timer.current = createKioskIdleTimer(setCountdown, onClose)
    return () => {
      timer.current?.stop()
      timer.current = null
      setCountdown(null)
    }
  }, [open, onClose])
  const reset = useCallback(() => timer.current?.reset(), [])
  const hold = useCallback((held: boolean) => timer.current?.hold(held), [])
  return { countdown, reset, hold }
}
